import BaseLayer from "ol/layer/Base"
import { ShapeLayer } from "../state"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import GeoJSON from "ol/format/GeoJSON"
import { bbox } from "ol/loadingstrategy"
import { Fill, Stroke, Style } from "ol/style"
import CircleStyle from "ol/style/Circle"
import { memoize } from "lodash"
import { Map as OlMap } from "ol"
import distinctColors from "distinct-colors"
import { getColorStops } from "./model_output"
import { findColor } from "../analysis_panel_tools/subsection"


const getSource = memoize((id: string, attribution: undefined | string, externalLink: undefined | string) => {

    const store = id.split(":")[0]
    const geoserverBase = externalLink ? externalLink.replace(/\/+$/, "") : "https://landscapes.wearepal.ai/geoserver"

    const source = new VectorSource({
        url: extent => `${geoserverBase}/${store}/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${id}&outputFormat=application/json&bbox=${extent.join(',')},EPSG:3857&srsName=EPSG:3857`,
        format: new GeoJSON({
            extractGeometryName: true
        }),
        strategy: bbox,
        attributions: attribution
    })

    return source
})

const fieldColorCache = new globalThis.Map<string, globalThis.Map<string, [number, number, number, number]>>()

function isNumericField(layer: ShapeLayer): boolean {
    const selected = layer.vectorFields?.find(field => field.name === layer.selectedField)
    const type = selected?.type?.toLowerCase() || ""
    return ["integer", "int", "real", "double", "float", "number", "numeric", "long"].some(token => type.includes(token))
}

function getValueColor(layer: ShapeLayer, value: string): [number, number, number, number] {
    const field = layer.selectedField || "__default__"
    const configuredColors = layer.valueColors?.[field]
    const values = (layer.fieldValues?.[field] || []).map(v => v.toString())
    const cacheKey = `${layer.identifier}:${field}:${JSON.stringify(values)}:${JSON.stringify(configuredColors || [])}`
    const cached = fieldColorCache.get(cacheKey)

    if (cached?.has(value)) {
        return cached.get(value)!
    }

    const distinctValues = Array.from(new Set(values.concat(value)))
    const palette = distinctColors({ count: Math.max(distinctValues.length, 1) }).map(color => color.rgba())
    const colorMap = new globalThis.Map<string, [number, number, number, number]>()

    distinctValues.forEach((entry, index) => {
        colorMap.set(entry, (configuredColors?.[index] || palette[index]) as [number, number, number, number])
    })

    fieldColorCache.set(cacheKey, colorMap)
    return colorMap.get(value) || [layer.colors.fill[0], layer.colors.fill[1], layer.colors.fill[2], layer.colors.fill[3]]
}

function applyLayerOpacity(color: [number, number, number, number], opacity: number): [number, number, number, number] {
    return [color[0], color[1], color[2], opacity]
}

const getStyle = (layer: ShapeLayer, zoom: number | undefined) => (
    (feature) => {
        const selectedField = layer.selectedField
        const rawValue = selectedField ? feature.get(selectedField) : undefined
        let fillColor: [number, number, number, number] = layer.colors.fill

        if (selectedField && (rawValue === undefined || rawValue === null || rawValue === "")) {
            fillColor = [0, 0, 0, 0]
        } else if (rawValue !== undefined && rawValue !== null && selectedField) {
            if (isNumericField(layer)) {
                const numericValue = Number(rawValue)
                const numericValues = (layer.fieldValues?.[selectedField] || [])
                    .map(value => Number(value))
                    .filter(value => !Number.isNaN(value))

                if (!Number.isNaN(numericValue) && numericValues.length > 0) {
                    const min = Math.min(...numericValues)
                    const max = Math.max(...numericValues)
                    const normalised = max === min ? 1 : (numericValue - min) / (max - min)
                    const colorMap = getColorStops(layer.fill === "heatmap" ? "jet" : (layer.fill === "greyscale" || !layer.fill ? "greys" : layer.fill), 100).reverse()
                    fillColor = findColor(normalised, colorMap)
                } else {
                    fillColor = [0, 0, 0, 0]
                }
            } else {
                fillColor = getValueColor(layer, rawValue.toString())
            }
        }

        fillColor = applyLayerOpacity(fillColor, layer.opacity)

        const fill = `rgba(${fillColor[0]}, ${fillColor[1]}, ${fillColor[2]}, ${fillColor[3]})`
        const stroke = new Stroke({
            color: `rgba(${layer.colors.stroke[0]}, ${layer.colors.stroke[1]}, ${layer.colors.stroke[2]}, ${layer.colors.stroke[3]})`,
            width: layer.colors.strokeWidth || 1
        })
        const geomType = feature.getGeometry()?.getType()

        if (geomType === "Point" || geomType === "MultiPoint") {
            if (fillColor[3] === 0) return undefined
            const outlineAlpha = layer.colors.stroke[3]
            return new Style({
                image: new CircleStyle({
                    radius: zoom && zoom > 16 ? 8 : 6,
                    fill: new Fill({ color: fill }),
                    stroke: outlineAlpha > 0
                        ? stroke
                        : new Stroke({ color: "rgba(255, 255, 255, 0.9)", width: 1.5 }),
                })
            })
        }

        return new Style({
            fill : new Fill({
                color: fill
            }),
            stroke
        })
    }
)

export function reifyShapeFileLayer (layer: ShapeLayer, existingLayer: BaseLayer | null, map: OlMap) {

    const vectLayer =  new VectorLayer({
        source: getSource(layer.identifier, layer.attribution, layer.externalLink),
        style: getStyle(layer, map.getView().getZoom()),
        minZoom: layer.minZoom,
    })

    return vectLayer

}